import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  FileText,
  Users,
  Eye,
  Send
} from 'lucide-react';

const WACampaignWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'immediate',
    template_id: '',
    group_id: '',
    message_content: '',
    media_url: '',
    scheduled_date: '',
    scheduled_time: '',
    batch_mode: false,
    batch_size: 10,
    batch_time_slots: [{ start_hour: 10, start_minute: 0 }, { start_hour: 17, start_minute: 0 }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, groupsRes] = await Promise.all([
        api.get('/wa/templates'),
        api.get('/wa/groups')
      ]);
      setTemplates(templatesRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setFormData({
      ...formData,
      template_id: templateId,
      message_content: template?.content || '',
      media_url: template?.media_url || ''
    });
  };

  const handleGroupChange = async (groupId) => {
    setFormData({ ...formData, group_id: groupId });
    
    try {
      const response = await api.get(`/wa/groups/${groupId}/members`);
      setGroupMembers(response.data);
    } catch (error) {
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Prepare campaign data
      const campaignData = {
        name: formData.name,
        type: formData.type,
        template_id: formData.template_id,
        group_id: formData.group_id,
        message_content: formData.message_content,
        media_url: formData.media_url,
        batch_mode: formData.batch_mode || false,
        batch_size: formData.batch_mode ? (formData.batch_size || 10) : 10,
        batch_time_slots: formData.batch_mode ? formData.batch_time_slots : []
      };
      
      // Add scheduled_at for scheduled campaigns
      if (formData.type === 'scheduled' && formData.scheduled_date) {
        if (formData.batch_mode && formData.batch_time_slots.length > 0) {
          // Use first time slot as the initial scheduled_at
          const firstSlot = formData.batch_time_slots[0];
          const h = String(firstSlot.start_hour).padStart(2, '0');
          const m = String(firstSlot.start_minute).padStart(2, '0');
          campaignData.scheduled_at = `${formData.scheduled_date}T${h}:${m}:00`;
        } else if (formData.scheduled_time) {
          campaignData.scheduled_at = `${formData.scheduled_date}T${formData.scheduled_time}:00`;
        }
      }
      
      // Create campaign
      const response = await api.post('/wa/campaigns', campaignData);
      const campaignId = response.data.id;
      
      // For immediate campaigns, navigate to queue
      // For scheduled campaigns, navigate to campaigns list
      if (formData.type === 'immediate') {
        navigate(`/whatsapp/campaigns/${campaignId}/queue`);
      } else {
        alert('Campaign scheduled successfully! It will start automatically at the scheduled time.');
        navigate('/whatsapp/campaigns');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error creating campaign';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    let preview = formData.message_content;
    preview = preview.replace(/\{\{name\}\}/g, 'John Doe');
    preview = preview.replace(/\{\{phone\}\}/g, '+60123456789');
    return preview;
  };

  const isStepValid = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (formData.name.trim() === '') return false;
        if (formData.type === 'scheduled') {
          if (formData.batch_mode) {
            // Batch mode only needs a start date + at least one time slot
            return formData.scheduled_date !== '' && formData.batch_time_slots.length > 0;
          }
          return formData.scheduled_date !== '' && formData.scheduled_time !== '';
        }
        return true;
      case 2:
        return formData.template_id !== '' && formData.message_content.trim() !== '';
      case 3:
        return formData.group_id !== '' && groupMembers.length > 0;
      default:
        return true;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/whatsapp/campaigns')}
        >
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Campaign</h1>
          <p className="text-gray-500">Set up your WhatsApp broadcast campaign</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center">
        {[1, 2, 3, 4].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= s 
                ? 'bg-[#4F46E5] text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              {step > s ? <Check size={20} /> : s}
            </div>
            {i < 3 && (
              <div className={`w-24 h-1 ${
                step > s ? 'bg-[#4F46E5]' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flex justify-center gap-8 text-sm text-gray-500">
        <span className={step >= 1 ? 'text-[#4F46E5] font-medium' : ''}>Details</span>
        <span className={step >= 2 ? 'text-[#4F46E5] font-medium' : ''}>Template</span>
        <span className={step >= 3 ? 'text-[#4F46E5] font-medium' : ''}>Recipients</span>
        <span className={step >= 4 ? 'text-[#4F46E5] font-medium' : ''}>Review</span>
      </div>

      {/* Step Content */}
      <Card className="p-6 bg-white border border-gray-100">
        {/* Step 1: Campaign Details */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Campaign Details</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., January Promotion"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Type *
              </label>
              <div className="space-y-3">
                <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer ${
                  formData.type === 'immediate' ? 'border-[#4F46E5] bg-[#4F46E5]/5' : 'border-gray-200'
                }`}>
                  <input
                    type="radio"
                    name="type"
                    value="immediate"
                    checked={formData.type === 'immediate'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Immediate (Ad Hoc)</p>
                    <p className="text-sm text-gray-500">Send messages now</p>
                  </div>
                </label>
                
                <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer ${
                  formData.type === 'scheduled' ? 'border-[#4F46E5] bg-[#4F46E5]/5' : 'border-gray-200'
                }`}>
                  <input
                    type="radio"
                    name="type"
                    value="scheduled"
                    checked={formData.type === 'scheduled'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Scheduled</p>
                    <p className="text-sm text-gray-500">Schedule for a specific date/time (Malaysia Time)</p>
                  </div>
                </label>
                
                <div 
                  className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                  onClick={() => navigate('/whatsapp/fixed-flows')}
                >
                  <div className="mt-1 w-4 h-4 rounded-full border-2 border-purple-400 flex items-center justify-center">
                    <ArrowRight size={10} className="text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Fixed Flow</p>
                    <p className="text-sm text-gray-500">Automated sequence for new clients (Day 1, 7, 30, 60, 90, 365)</p>
                    <p className="text-xs text-purple-600 mt-1">Click to manage Fixed Flows →</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Schedule Date/Time for scheduled campaigns */}
            {formData.type === 'scheduled' && !formData.batch_mode && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h3 className="text-sm font-medium text-blue-900 mb-3">Schedule Settings (Malaysia Time - UTC+8)</h3>
                <p className="text-xs text-blue-700 mb-4">Note: Campaign must be scheduled at least 1 hour in advance</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <Input
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="scheduled-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time *
                    </label>
                    <Input
                      type="time"
                      value={formData.scheduled_time}
                      onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                      data-testid="scheduled-time"
                    />
                  </div>
                </div>
                
                {formData.scheduled_date && formData.scheduled_time && (
                  <p className="mt-3 text-sm text-blue-700">
                    Campaign will start at: <strong>{formData.scheduled_date} {formData.scheduled_time} (Malaysia Time)</strong>
                  </p>
                )}
              </div>
            )}

            {/* Batch Mode for scheduled campaigns */}
            {formData.type === 'scheduled' && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Batch Mode</h3>
                    <p className="text-xs text-gray-500">Split large groups into smaller batches sent across multiple time slots and days</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.batch_mode}
                      onChange={(e) => setFormData({ ...formData, batch_mode: e.target.checked })}
                      className="sr-only peer"
                      data-testid="batch-mode-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4F46E5]"></div>
                  </label>
                </div>

                {formData.batch_mode && (
                  <div className="space-y-4 mt-4 pt-4 border-t border-gray-200">
                    {/* Start Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (Malaysia Time)</label>
                      <Input
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-48"
                        data-testid="batch-start-date"
                      />
                      <p className="text-xs text-gray-400 mt-1">First batch will be sent on this date at the first time slot</p>
                    </div>

                    {/* Batch Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Clients per batch</label>
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        value={formData.batch_size}
                        onChange={(e) => setFormData({ ...formData, batch_size: parseInt(e.target.value) || 10 })}
                        className="w-32"
                        data-testid="batch-size-input"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {groupMembers.length > 0 && (
                          <>{groupMembers.length} clients ÷ {formData.batch_size} = {Math.ceil(groupMembers.length / formData.batch_size)} batches</>
                        )}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Time Slots (Malaysia Time)</label>
                      <div className="space-y-2">
                        {formData.batch_time_slots.map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-14">Slot {idx + 1}</span>
                            <Input
                              type="time"
                              value={`${String(slot.start_hour).padStart(2, '0')}:${String(slot.start_minute).padStart(2, '0')}`}
                              onChange={(e) => {
                                const [h, m] = e.target.value.split(':').map(Number);
                                const updated = [...formData.batch_time_slots];
                                updated[idx] = { start_hour: h, start_minute: m };
                                setFormData({ ...formData, batch_time_slots: updated });
                              }}
                              className="w-36"
                              data-testid={`batch-slot-time-${idx}`}
                            />
                            {formData.batch_time_slots.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = formData.batch_time_slots.filter((_, i) => i !== idx);
                                  setFormData({ ...formData, batch_time_slots: updated });
                                }}
                                className="text-red-500 hover:text-red-700 h-8 px-2"
                                data-testid={`remove-slot-${idx}`}
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, batch_time_slots: [...formData.batch_time_slots, { start_hour: 14, start_minute: 0 }] })}
                        className="mt-2 text-xs"
                        data-testid="add-time-slot-btn"
                      >
                        + Add Time Slot
                      </Button>
                    </div>

                    {groupMembers.length > 0 && formData.batch_time_slots.length > 0 && (
                      <div className="bg-white rounded-lg border border-gray-200 p-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">Schedule Preview</p>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {(() => {
                            const totalBatches = Math.ceil(groupMembers.length / formData.batch_size);
                            const slots = formData.batch_time_slots;
                            const rows = [];
                            for (let b = 0; b < totalBatches; b++) {
                              const slotIdx = b % slots.length;
                              const dayNum = Math.floor(b / slots.length) + 1;
                              const slot = slots[slotIdx];
                              const start = b * formData.batch_size + 1;
                              const end = Math.min((b + 1) * formData.batch_size, groupMembers.length);
                              rows.push(
                                <div key={b} className="flex items-center gap-2 text-xs">
                                  <span className="text-gray-400 w-16">Day {dayNum}</span>
                                  <span className="text-[#4F46E5] font-medium w-16">
                                    {String(slot.start_hour).padStart(2, '0')}:{String(slot.start_minute).padStart(2, '0')}
                                  </span>
                                  <span className="text-gray-600">Batch {b + 1} — Clients {start}–{end}</span>
                                </div>
                              );
                            }
                            return rows;
                          })()}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Total: {Math.ceil(groupMembers.length / formData.batch_size)} batches over {Math.ceil(Math.ceil(groupMembers.length / formData.batch_size) / formData.batch_time_slots.length)} day(s)
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Choose & Edit Template */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Choose & Edit Template</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Template *
              </label>
              <Select value={formData.template_id} onValueChange={handleTemplateChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        <FileText size={14} />
                        {template.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-sm text-yellow-600 mt-1">
                  No templates found. Please create a template first.
                </p>
              )}
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Message Content *
                </label>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500">Variables:</span>
                  <code className="bg-gray-100 px-1 rounded">{'{{name}}'}</code>
                  <code className="bg-gray-100 px-1 rounded">{'{{phone}}'}</code>
                </div>
              </div>
              <Textarea
                value={formData.message_content}
                onChange={(e) => setFormData({ ...formData, message_content: e.target.value })}
                placeholder="Select a template or write your message..."
                rows={8}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Media URL (Optional)
              </label>
              <Input
                value={formData.media_url}
                onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                placeholder="https://example.com/image.jpg or YouTube link"
              />
            </div>
          </div>
        )}

        {/* Step 3: Choose Recipients */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Choose Recipients</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Client Group *
              </label>
              <Select value={formData.group_id} onValueChange={handleGroupChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      <div className="flex items-center gap-2">
                        <Users size={14} />
                        {group.name} ({group.member_count} members)
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {groups.length === 0 && (
                <p className="text-sm text-yellow-600 mt-1">
                  No groups found. Please create a client group first.
                </p>
              )}
            </div>
            
            {groupMembers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview Recipients ({groupMembers.length} total)
                </label>
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Phone</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {groupMembers.slice(0, 10).map((member) => (
                        <tr key={member.id}>
                          <td className="px-3 py-2 text-gray-900">{member.client_name}</td>
                          <td className="px-3 py-2 text-gray-600">{member.client_phone}</td>
                          <td className="px-3 py-2">
                            <span className="text-green-600">✓ Will receive</span>
                          </td>
                        </tr>
                      ))}
                      {groupMembers.length > 10 && (
                        <tr>
                          <td colSpan="3" className="px-3 py-2 text-center text-gray-500">
                            ... and {groupMembers.length - 10} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Review & Confirm</h2>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Campaign Name:</span>
                <span className="font-medium text-gray-900">{formData.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Type:</span>
                <span className="font-medium text-gray-900 capitalize">{formData.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Recipients:</span>
                <span className="font-medium text-gray-900">{groupMembers.length} clients</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Media:</span>
                <span className="font-medium text-gray-900">
                  {formData.media_url ? '1 attachment' : 'None'}
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Preview
              </label>
              <div className="bg-[#DCF8C6] rounded-lg p-4 max-w-md">
                <p className="whitespace-pre-wrap text-sm">{renderPreview()}</p>
                {formData.media_url && (
                  <p className="mt-2 text-blue-600 text-xs truncate">
                    📎 {formData.media_url}
                  </p>
                )}
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                ⚠️ This will create {groupMembers.length} messages ready to send. You can start sending from the next screen.
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button 
          variant="outline"
          onClick={() => setStep(step - 1)}
          disabled={step === 1}
        >
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
        
        {step < 4 ? (
          <Button 
            onClick={() => setStep(step + 1)}
            disabled={!isStepValid(step)}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
          >
            Next
            <ArrowRight size={16} className="ml-2" />
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
          >
            {loading ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <Send size={16} className="mr-2" />
            )}
            Create Campaign
          </Button>
        )}
      </div>
    </div>
  );
};

export default WACampaignWizard;
